/**
 * Geocoding utility.
 * Mapbox (when VITE_MAPBOX_ACCESS_TOKEN is set) - South Africa only (country=ZA).
 * Fallback: Photon + Nominatim. Free, no API key.
 */

const MAPBOX_BASE = 'https://api.mapbox.com/geocoding/v5/mapbox.places';
const GOOGLE_GEOCODE_BASE = 'https://maps.googleapis.com/maps/api/geocode/json';
const GOOGLE_PLACES_AUTOCOMPLETE_BASE = 'https://maps.googleapis.com/maps/api/place/autocomplete/json';
const PHOTON_BASE = 'https://photon.komoot.io/api';
const NOMINATIM_BASE = 'https://nominatim.openstreetmap.org/search';
const DEFAULT_BOUNDS = { south: -35, west: 16, north: -22, east: 33 }; // South Africa
const FETCH_TIMEOUT_MS = 6000;

const MAPBOX_TOKEN = typeof import.meta !== 'undefined' && import.meta.env?.VITE_MAPBOX_ACCESS_TOKEN?.trim();
const GOOGLE_API_KEY = typeof import.meta !== 'undefined' && import.meta.env?.VITE_GOOGLE_MAPS_API_KEY?.trim();

function fetchWithTimeout(url, options = {}) {
  const { timeout = FETCH_TIMEOUT_MS, headers = {}, ...rest } = typeof options === 'number' ? { timeout: options } : options;
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);
  return fetch(url, {
    headers: { Accept: 'application/json', ...headers },
    signal: controller.signal,
    ...rest,
  }).finally(() => clearTimeout(id));
}

/**
 * Google Places Autocomplete (South Africa only)
 */
async function searchPlacesGoogle(query, limit = 5) {
  if (!GOOGLE_API_KEY) return null;
  const input = encodeURIComponent(query.trim());
  const url = `${GOOGLE_PLACES_AUTOCOMPLETE_BASE}?input=${input}&components=country:za&types=geocode&key=${GOOGLE_API_KEY}`;
  const res = await fetchWithTimeout(url, FETCH_TIMEOUT_MS);
  const data = await res.json();
  if (!data?.predictions?.length) return [];
  // For autocomplete we mainly need display text; lat/lon is optional
  return data.predictions.slice(0, limit).map((p) => ({
    display_name: p.description,
    lat: null,
    lon: null,
    place_id: p.place_id,
  }));
}

/**
 * Google Geocode (South Africa only)
 */
async function geocodePlaceGoogle(query) {
  if (!GOOGLE_API_KEY) return null;
  const address = encodeURIComponent(query.trim() + ', South Africa');
  const url = `${GOOGLE_GEOCODE_BASE}?address=${address}&components=country:ZA&key=${GOOGLE_API_KEY}`;
  const res = await fetchWithTimeout(url, FETCH_TIMEOUT_MS);
  const data = await res.json();
  if (data.status !== 'OK' || !data.results?.length) return null;
  const first = data.results[0];
  const loc = first.geometry?.location || {};
  const terms = [];
  const seen = new Set();
  (first.address_components || []).forEach((comp) => {
    if (comp.long_name) {
      const v = comp.long_name.trim();
      if (v && !seen.has(v.toLowerCase())) {
        seen.add(v.toLowerCase());
        terms.push(v);
      }
    }
  });
  return {
    terms: terms.length > 0 ? terms : [query.trim()],
    display_name: first.formatted_address || query.trim(),
    lat: typeof loc.lat === 'number' ? loc.lat : null,
    lon: typeof loc.lng === 'number' ? loc.lng : null,
  };
}

/**
 * Mapbox search - restricted to South Africa (country=ZA)
 */
async function searchPlacesMapbox(query, limit = 5) {
  if (!MAPBOX_TOKEN) return null;
  const q = (query.trim() + ', South Africa').trim();
  const encoded = encodeURIComponent(q);
  const url = `${MAPBOX_BASE}/${encoded}.json?access_token=${MAPBOX_TOKEN}&country=ZA&limit=${limit}&types=place,locality,neighborhood,address`;
  const res = await fetchWithTimeout(url, FETCH_TIMEOUT_MS);
  const data = await res.json();
  if (!data?.features?.length) return [];
  return data.features.map((f) => {
    const [lon, lat] = f.geometry?.coordinates || [null, null];
    const display_name = f.place_name || f.text || 'Unknown';
    const ctx = Array.isArray(f.context) ? f.context : [];
    const region = ctx.find((c) => c.id?.startsWith('region'));
    const place = ctx.find((c) => c.id?.startsWith('place'));
    return {
      display_name,
      lat: lat ?? null,
      lon: lon ?? null,
      name: f.text || f.place_name,
      city: place?.text || f.text,
      state: region?.text,
      country: 'South Africa',
    };
  });
}

/**
 * Photon search (fallback)
 */
async function searchPlacesPhoton(query, limit = 5) {
  const q = query.trim() + ', South Africa';
  const encoded = encodeURIComponent(q);
  const bbox = `${DEFAULT_BOUNDS.south},${DEFAULT_BOUNDS.west},${DEFAULT_BOUNDS.north},${DEFAULT_BOUNDS.east}`;
  const res = await fetchWithTimeout(`${PHOTON_BASE}/?q=${encoded}&limit=${limit}&bbox=${bbox}&lang=en`, FETCH_TIMEOUT_MS);
  const data = await res.json();
  if (!data?.features?.length) return [];
  return data.features.map((f) => {
    const p = f.properties || {};
    const [lon, lat] = f.geometry?.coordinates || [null, null];
    const parts = [p.name, p.city, p.state, p.country].filter(Boolean);
    const display_name = parts.join(', ') || p.name || 'Unknown';
    return {
      display_name,
      lat: lat ?? null,
      lon: lon ?? null,
      name: p.name,
      city: p.city,
      state: p.state,
      country: p.country,
      county: p.county,
    };
  });
}

/**
 * Nominatim search (fallback)
 */
async function searchPlacesNominatim(query, limit = 5) {
  const q = query.trim() + ', South Africa';
  const encoded = encodeURIComponent(q);
  const res = await fetchWithTimeout(`${NOMINATIM_BASE}?q=${encoded}&format=json&limit=${limit}&countrycodes=za`, {
    timeout: FETCH_TIMEOUT_MS,
    headers: { 'User-Agent': 'MrsCater/1.0 (location autocomplete)' },
  });
  const data = await res.json();
  if (!Array.isArray(data) || data.length === 0) return [];
  return data.map((item) => {
    const parts = [item.name, item.address?.city || item.address?.town || item.address?.village, item.address?.state, item.address?.country].filter(Boolean);
    const display_name = parts.join(', ') || item.display_name || 'Unknown';
    return {
      display_name,
      lat: item.lat ? parseFloat(item.lat) : null,
      lon: item.lon ? parseFloat(item.lon) : null,
      name: item.name,
      city: item.address?.city || item.address?.town || item.address?.village,
      state: item.address?.state,
      country: item.address?.country,
    };
  });
}

/**
 * Search places for autocomplete (debounce externally)
 * Mapbox primary when API key set (South Africa only). Photon/Nominatim fallback.
 */
export async function searchPlaces(query, limit = 5) {
  if (!query || typeof query !== 'string' || !query.trim()) return [];
  // Browser CORS can block Google Places REST endpoints.
  // Try providers in order and fall back gracefully.
  if (GOOGLE_API_KEY) {
    try {
      const googleResults = await searchPlacesGoogle(query, limit);
      if (Array.isArray(googleResults) && googleResults.length > 0) return googleResults;
    } catch (err) {
      console.warn('[geocoding] searchPlacesGoogle failed, falling back:', err?.message || err);
    }
  }
  try {
    const mapboxResults = await searchPlacesMapbox(query, limit);
    if (Array.isArray(mapboxResults) && mapboxResults.length > 0) return mapboxResults;
  } catch (err) {
    console.warn('[geocoding] searchPlacesMapbox failed, falling back:', err?.message || err);
  }
  try {
    const photonResults = await searchPlacesPhoton(query, limit);
    if (Array.isArray(photonResults) && photonResults.length > 0) return photonResults;
  } catch (err) {
    console.warn('[geocoding] searchPlacesPhoton failed, falling back:', err?.message || err);
  }
  try {
    const nominatimResults = await searchPlacesNominatim(query, limit);
    if (Array.isArray(nominatimResults) && nominatimResults.length > 0) return nominatimResults;
  } catch (err) {
    console.warn('[geocoding] searchPlacesNominatim failed:', err?.message || err);
  }
  return [];
}

/**
 * Mapbox geocode - restricted to South Africa
 */
async function geocodePlaceMapbox(query) {
  if (!MAPBOX_TOKEN) return null;
  const q = (query.trim() + ', South Africa').trim();
  const encoded = encodeURIComponent(q);
  const url = `${MAPBOX_BASE}/${encoded}.json?access_token=${MAPBOX_TOKEN}&country=ZA&limit=3&types=place,locality,neighborhood,region`;
  const res = await fetchWithTimeout(url, FETCH_TIMEOUT_MS);
  const data = await res.json();
  if (!data?.features?.length) return null;
  const seen = new Set();
  const terms = [];
  for (const f of data.features) {
    const ctx = Array.isArray(f.context) ? f.context : [];
    for (const c of ctx) {
      const v = c?.text;
      if (v && typeof v === 'string') {
        const n = v.trim();
        if (n && !seen.has(n.toLowerCase())) {
          seen.add(n.toLowerCase());
          terms.push(n);
        }
      }
    }
    if (f.text) {
      const n = f.text.trim();
      if (n && !seen.has(n.toLowerCase())) {
        seen.add(n.toLowerCase());
        terms.push(n);
      }
    }
  }
  const first = data.features[0];
  const [lon, lat] = first?.geometry?.coordinates || [null, null];
  return {
    terms: terms.length > 0 ? terms : [query.trim()],
    display_name: first?.place_name || query.trim(),
    lat: lat ?? null,
    lon: lon ?? null,
  };
}

/**
 * Geocode a place and extract search terms for matching caterers.
 * E.g. "Soweto" -> ["Soweto", "Johannesburg", "Gauteng"]
 */
export async function geocodePlace(query) {
  if (!query || typeof query !== 'string' || !query.trim()) return { terms: [] };
  const q = query.trim();
  const simpleFallback = { terms: [q], display_name: q, lat: null, lon: null };
  if (GOOGLE_API_KEY) {
    try {
      const g = await geocodePlaceGoogle(q);
      if (g) return g;
    } catch (error) {
      console.warn('[geocoding] geocodePlaceGoogle failed, falling back:', error?.message || error);
    }
  }
  try {
    const m = await geocodePlaceMapbox(q);
    if (m) return m;
  } catch (error) {
    console.warn('[geocoding] geocodePlaceMapbox failed, falling back:', error?.message || error);
  }
  try {
    const [p] = await searchPlacesPhoton(q, 1);
    if (p) {
      const terms = [p.name, p.city, p.state, p.county].filter(Boolean);
      return {
        terms: terms.length > 0 ? terms : [q],
        display_name: p.display_name || q,
        lat: p.lat ?? null,
        lon: p.lon ?? null,
      };
    }
  } catch (error) {
    console.warn('[geocoding] geocode via Photon failed, falling back:', error?.message || error);
  }
  try {
    const [n] = await searchPlacesNominatim(q, 1);
    if (n) {
      const terms = [n.name, n.city, n.state].filter(Boolean);
      return {
        terms: terms.length > 0 ? terms : [q],
        display_name: n.display_name || q,
        lat: n.lat ?? null,
        lon: n.lon ?? null,
      };
    }
  } catch (error) {
    console.warn('[geocoding] geocode via Nominatim failed:', error?.message || error);
  }
  return simpleFallback;
}

/**
 * Reverse geocode: lat/lon -> display name and terms (for "Use my location")
 */
export async function reverseGeocode(lat, lon) {
  if (lat == null || lon == null || typeof lat !== 'number' || typeof lon !== 'number') {
    return { terms: [], display_name: '' };
  }
  const PHOTON_REVERSE = 'https://photon.komoot.io/reverse';
  const NOMINATIM_REVERSE = 'https://nominatim.openstreetmap.org/reverse';
  try {
    const res = await fetchWithTimeout(
      `${PHOTON_REVERSE}?lat=${lat}&lon=${lon}&lang=en`,
      FETCH_TIMEOUT_MS
    );
    const data = await res.json();
    const f = data?.features?.[0];
    if (!f?.properties) {
      throw new Error('No result');
    }
    const p = f.properties;
    const parts = [p.name, p.city, p.state, p.country].filter(Boolean);
    const display_name = parts.join(', ') || `${lat.toFixed(4)}, ${lon.toFixed(4)}`;
    const terms = [p.name, p.city, p.state, p.county].filter(Boolean);
    const [lng, latCoord] = f.geometry?.coordinates || [lon, lat];
    return {
      terms: terms.length > 0 ? terms : [display_name],
      display_name,
      lat: latCoord ?? lat,
      lon: lng ?? lon,
    };
  } catch (e) {
    console.warn('[geocoding] Photon reverseGeocode failed:', e?.message || e);
  }
  try {
    const res = await fetchWithTimeout(
      `${NOMINATIM_REVERSE}?lat=${lat}&lon=${lon}&format=json`,
      { headers: { 'User-Agent': 'MrsCater/1.0 (reverse geocode)' } }
    );
    const data = await res.json();
    const addr = data?.address || {};
    const parts = [
      addr.city || addr.town || addr.village || addr.suburb,
      addr.state,
      addr.country,
    ].filter(Boolean);
    const display_name = data?.display_name || parts.join(', ') || `${lat.toFixed(4)}, ${lon.toFixed(4)}`;
    const terms = [addr.city, addr.town, addr.village, addr.state, addr.county].filter(Boolean);
    return {
      terms: terms.length > 0 ? terms : [display_name],
      display_name,
      lat: data?.lat ? parseFloat(data.lat) : lat,
      lon: data?.lon ? parseFloat(data.lon) : lon,
    };
  } catch (e2) {
    console.warn('[geocoding] Nominatim reverseGeocode failed:', e2?.message || e2);
    return {
      terms: [],
      display_name: `${lat.toFixed(4)}, ${lon.toFixed(4)}`,
      lat,
      lon,
    };
  }
}
